import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  FaCalculator,
  FaInfoCircle,
  FaArrowRight,
  FaMoneyBillWave,
  FaCalendarAlt,
} from 'react-icons/fa';

gsap.registerPlugin(ScrollTrigger);

const DEBOUNCE_MS = 500;
const MACC_FEE = 400;

const formatCurrency = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const LoanCosts = () => {
  const sectionRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const cacheRef = useRef(new Map());

  const [loanAmount, setLoanAmount] = useState(2500);
  const [loanTerm, setLoanTerm] = useState(24);

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);

  const calcType = useMemo(
    () => (loanAmount < 2000 ? 'SACC' : 'MACC'),
    [loanAmount]
  );

  const allTerms = [6, 12, 18, 24];

  useEffect(() => {
    if (calcType === 'SACC' && loanTerm > 12) {
      setLoanTerm(12);
    }
  }, [calcType, loanTerm]);

  const applyResult = useCallback((data) => {
    setSummary(data);
    setStale(false);
  }, []);

  const fetchCalculation = useCallback(
    async (amount, term, cacheKey) => {
      if (abortRef.current) abortRef.current.abort();

      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);

      try {
        const durationWeeks = Math.round(term * 4.33);

        const res = await fetch('/api/calculate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            principal: amount,
            duration: durationWeeks,
          }),
          signal: controller.signal,
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || 'Calculation failed');
        }

        const result = data?.repaymentSummary;
        cacheRef.current.set(cacheKey, result);
        applyResult(result);
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [applyResult]
  );

  useEffect(() => {
    const cacheKey = `${loanAmount}_${loanTerm}`;
    setStale(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const cached = cacheRef.current.get(cacheKey);

      if (cached) {
        applyResult(cached);
        return;
      }

      fetchCalculation(loanAmount, loanTerm, cacheKey);
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [loanAmount, loanTerm, fetchCalculation, applyResult]);

  useEffect(() => {
    gsap.fromTo(
      '.cost-animate',
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
        },
      }
    );
  }, []);

  const rawPayment = summary?.payment;
  const totalRepaid = summary?.totalRepaid;
  const payments = summary?.noOfPayments;
  const frequency = summary?.frequencyLabel ?? '';

  const isFortnightly = frequency.toLowerCase().includes('fortnight');

  const weeklyPayment =
    rawPayment != null
      ? isFortnightly
        ? rawPayment / 2
        : rawPayment
      : null;

  const fortnightlyPayment =
    rawPayment != null && isFortnightly ? rawPayment : null;

  let statutoryFee = 0;

  if (calcType === 'SACC') {
    statutoryFee = Math.round(loanAmount * 0.2 * 100) / 100;
  } else {
    statutoryFee = MACC_FEE;
  }

  const totalCost =
    totalRepaid != null
      ? Math.max(totalRepaid - loanAmount, 0)
      : null;

  const interestCost =
    totalCost != null
      ? Math.max(totalCost - statutoryFee, 0)
      : null;

  return (
    <div ref={sectionRef} className="min-h-screen bg-white pt-32 pb-20">

      <section className="container mx-auto px-4 lg:px-8 mb-12 text-center max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-2 bg-secondary/20 text-primary font-semibold px-4 py-2 rounded-full mb-6">
            <FaCalculator />
            Loan Calculator
          </div>
          <h1 className="font-display text-5xl font-bold mb-6">
            How Much Will I <span className="text-gradient">Pay Back?</span>
          </h1>
          <p className="text-gray-600">
            See your estimated repayments calculated in real time.
          </p>
        </motion.div>
      </section>

      <div className="container mx-auto px-4 lg:px-8 max-w-6xl">

        <div className="bg-yellow-100 border border-yellow-300 rounded-2xl p-6 mb-8 flex items-start gap-3">
          <FaInfoCircle className="text-yellow-700 mt-1" />
          <p className="text-yellow-900 text-sm font-medium">
            This calculator provides indicative estimates only. Actual rates may vary.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">

          <div className="glass-card p-8 md:p-10 cost-animate relative">

            <div className="absolute top-4 right-4 text-xs font-bold px-3 py-1 rounded-full bg-primary/10 text-primary">
              {calcType}
            </div>

            <div className="flex items-center gap-3 mb-8">
              <FaCalculator className="text-primary text-xl" />
              <h2 className="font-display text-2xl font-bold">Loan Calculator</h2>
            </div>

            <div className="mb-8">
              <div className="flex justify-between mb-3">
                <label>Loan Amount</label>
                <span className="font-bold">${loanAmount.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="2000"
                max="5000"
                step="100"
                value={loanAmount}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>$2,000</span>
                <span>$5,000</span>
              </div>
            </div>

            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm font-medium text-gray-600">
                  Loan Term
                </label>
                <span className="text-primary font-bold">
                  {loanTerm} months
                </span>
              </div>

              <div className="grid grid-cols-4 gap-2">
                {allTerms.map((term) => {
                  const disabled =
                    calcType === 'SACC' && term > 12;

                  return (
                    <button
                      key={term}
                      disabled={disabled}
                      onClick={() => !disabled && setLoanTerm(term)}
                      className={`py-2 rounded-lg text-sm font-medium transition-all ${
                        disabled
                          ? 'bg-gray-100 text-gray-300 cursor-not-allowed opacity-40'
                          : loanTerm === term
                          ? 'bg-primary text-white shadow-lg'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {term}m
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-primary rounded-2xl p-6 text-white">
              <p className="opacity-80 text-sm">
                Estimated Weekly Payment
              </p>

              <p
                className={`text-4xl font-bold transition-opacity ${
                  stale || loading ? 'opacity-60' : 'opacity-100'
                }`}
              >
                {weeklyPayment != null
                  ? `${formatCurrency(weeklyPayment)} / week`
                  : '—'}
              </p>

              <div className="h-6 mt-1">
                {fortnightlyPayment != null ? (
                  <p className="text-sm opacity-80">
                    Equivalent to {formatCurrency(fortnightlyPayment)} debited fortnightly
                  </p>
                ) : (
                  <p className="text-sm opacity-0">placeholder</p>
                )}
              </div>
            </div>

          </div>

          <div className="space-y-6 cost-animate">

            <div className="glass-card p-8">
              <h3 className="font-display text-xl font-bold mb-6 flex gap-2 items-center">
                <FaCalendarAlt /> Repayment Summary
              </h3>

              <div className="space-y-4">
                <Row label="Number of Payments" value={payments ?? '—'} />
                <Row
                  label="Total Repayable"
                  value={totalRepaid != null ? formatCurrency(totalRepaid) : '—'}
                />
              </div>
            </div>

            <div className="glass-card p-8">
              <h3 className="font-display text-xl font-bold mb-6 flex gap-2 items-center">
                <FaMoneyBillWave /> Loan Breakdown
              </h3>

              <div className="space-y-4">
                <Row label="Principal" value={formatCurrency(loanAmount)} />
                <Row label="Establishment Fee" value={formatCurrency(statutoryFee)} />
                <Row
                  label="Interest & Charges"
                  value={interestCost != null ? formatCurrency(interestCost) : '—'}
                />
                <Row
                  label="Total Cost"
                  value={totalCost != null ? formatCurrency(totalCost) : '—'}
                  highlight
                />
              </div>

              <a href="/apply-now" className="mt-6 block">
                <button className="w-full bg-primary text-white py-4 rounded-full flex justify-center gap-2">
                  Apply Now <FaArrowRight />
                </button>
              </a>
            </div>

            {error && (
              <div className="text-red-600 text-sm">
                {error}
              </div>
            )}

          </div>

        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, highlight }) => (
  <div className="flex justify-between items-center">
    <span className="text-gray-600">{label}</span>
    <span className={`font-bold ${highlight ? 'text-primary text-lg' : ''}`}>
      {value}
    </span>
  </div>
);

export default LoanCosts;
